/*
 * Purpose: Goal-centric context for Map AI Assistant — GOAL SSOT + fulfillment blocks
 * Last modified: 2026-07-10 | Agent
 * Completeness: 98
 */
import type { Node, Workflow } from '@/stores/workflowStore';
import { getBlockTypeLabel } from '@/lib/blockTypes';
import { getMissionGoalIntent, getNodePurposeLine } from '@/lib/mapNodeSummary';
import { getNodeLabel } from '@/lib/workflowUtils';

export function isFulfillmentBlock(node: Node): boolean {
  return (
    node.type === 'trigger' ||
    node.type === 'tool' ||
    node.type === 'action' ||
    node.type === 'agent' ||
    node.type === 'drone'
  );
}

/** Topological order from START through linked blocks. */
export function orderedMapNodes(workflow: Workflow): Node[] {
  const { nodes, edges } = workflow;
  if (nodes.length === 0) return [];

  const outEdges = new Map<string, string[]>();
  const inDegree = new Map<string, number>();
  for (const node of nodes) {
    outEdges.set(node.id, []);
    inDegree.set(node.id, 0);
  }
  for (const edge of edges) {
    outEdges.get(edge.from)?.push(edge.to);
    inDegree.set(edge.to, (inDegree.get(edge.to) ?? 0) + 1);
  }

  const startId =
    nodes.find((n) => n.type === 'start')?.id ??
    nodes.find((n) => (inDegree.get(n.id) ?? 0) === 0)?.id ??
    nodes[0].id;

  const order: Node[] = [];
  const seen = new Set<string>();
  const queue = [startId];

  while (queue.length > 0) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const node = nodes.find((n) => n.id === id);
    if (node) order.push(node);
    for (const next of outEdges.get(id) ?? []) {
      if (!seen.has(next)) queue.push(next);
    }
  }

  for (const node of nodes) {
    if (!seen.has(node.id)) order.push(node);
  }

  return order;
}

function goalNode(workflow: Workflow): Node | undefined {
  return workflow.nodes.find((n) => n.type === 'goal' && n.subtype === 'outcome');
}

function serializeBlockConfig(node: Node): Record<string, unknown> {
  const cfg = node.config ?? {};
  const pick = [
    'name',
    'outcome',
    'blockIntent',
    'taskOutcome',
    'categoryRole',
    'mcpGoal',
    'mcpToolName',
    'connectorId',
    'message',
    'gmailQuery',
    'source',
    'method',
    'url',
    'transform',
    'riskLevel',
  ] as const;

  const out: Record<string, unknown> = {};
  for (const key of pick) {
    const val = cfg[key];
    if (val !== undefined && val !== null && String(val).trim() !== '') {
      out[key] = val;
    }
  }
  return out;
}

function formatFulfillmentBlock(node: Node, index: number): string {
  const type = getBlockTypeLabel(node.type, node.subtype);
  const label = getNodeLabel(node);
  const purpose = getNodePurposeLine(node);
  const config = serializeBlockConfig(node);
  const configJson = Object.keys(config).length > 0 ? JSON.stringify(config) : '{}';

  return [
    `${index + 1}. nodeId=${node.id} [${type}] "${label}"`,
    `   fulfills GOAL via: ${purpose}`,
    `   editable config: ${configJson}`,
    `   (when refining: reuse nodeId=${node.id} as step.id to edit this block in place)`,
  ].join('\n');
}

/**
 * SSOT context for Map AI chat — GOAL block first, then blocks that fulfill it.
 * Planner and assistant use this to edit flow structure and block content.
 */
export function buildGoalCentricMapContext(workflow: Workflow | null | undefined): string {
  if (!workflow || workflow.nodes.length === 0) {
    return [
      'MISSION CONTEXT',
      'GOAL: (not set — user must describe outcome on GOAL block or in chat)',
      'FULFILLMENT BLOCKS: none — design blocks from the GOAL when user approves.',
    ].join('\n');
  }

  const goal = goalNode(workflow);
  const goalOutcome = String(goal?.config?.outcome ?? '').trim();
  const goalIntent = getMissionGoalIntent(workflow);
  const ordered = orderedMapNodes(workflow);
  const fulfillment = ordered.filter(isFulfillmentBlock);
  const edgeLines = workflow.edges.map((e) => `${e.from} → ${e.to}`);

  const goalSection = goal
    ? [
        `GOAL nodeId=${goal.id}`,
        `outcome: ${goalOutcome || '(empty — infer from chat until user sets GOAL)'}`,
        `display intent: ${goalIntent}`,
      ].join('\n')
    : `GOAL: ${goalIntent}`;

  const blocksSection =
    fulfillment.length > 0
      ? fulfillment.map((n, i) => formatFulfillmentBlock(n, i)).join('\n\n')
      : 'None yet — add triggers/tools/actions wired from START → GOAL.';

  return [
    'MISSION CONTEXT (GOAL is SSOT — fulfillment blocks exist to achieve it)',
    '',
    goalSection,
    '',
    `FULFILLMENT BLOCKS (${fulfillment.length}) — edit content via blockIntent, taskOutcome, config:`,
    blocksSection,
    '',
    `WIRING (${workflow.edges.length} links): ${edgeLines.slice(0, 32).join('; ') || 'none'}`,
    '',
    'Rules for assistant:',
    '- Proposed changes must advance the GOAL outcome.',
    '- To edit a block in place: set step.id = that block nodeId and update blockIntent/taskOutcome/config.',
    '- To add blocks: new step ids; wire order in steps[].',
    '- MCP blocks need blockIntent + taskOutcome + mcpGoal aligned to GOAL.',
  ].join('\n');
}

/** Legacy alias — map assistant and planner import this path. */
export function describeGoalCentricWorkflow(workflow: Workflow | null | undefined): string {
  return buildGoalCentricMapContext(workflow);
}
